/**
 * Servicio de Conexiones Internas
 * Motor de lógica para gestionar sangrado de fibras, splitters y conexiones
 * @module services/connectionService
 */

import { 
  ConexionInterna, 
  OrigenConexion,
  DestinoConexion,
  ValidacionConexiones,
  ErrorConexion,
  AdvertenciaConexion,
  generarIdConexion
} from '../types/connections'
import { 
  SplitterCompleto, 
  SplitterPortSalida,
  SplitterEntrada,
  RatioSplitter,
  TipoSplitter,
  RatioDesbalanceado,
  NivelSplitter,
  PuertoNAP
} from '../types/nodes'
import { ColorFibra, ColorBuffer, Fibra, EstadoFibra, EstadoExtremoFibra } from '../types/fibers'
import { connectionServiceApi, CreateConnectionData } from './connectionServiceApi'
import { cableServiceApi } from './cableServiceApi'
import { cableEndpointAtNode } from '../utils/cableEndpoints'

// ============================================================================
// CONSTANTES TIA-598
// ============================================================================

export const TIA_598_COLORES: ColorFibra[] = [
  'azul', 'naranja', 'verde', 'marron', 'gris', 'blanco',
  'rojo', 'negro', 'amarillo', 'violeta', 'rosa', 'turquesa'
]

export const ATENUACION_SPLITTER: Record<RatioSplitter, number> = {
  '1:2': 3.5,
  '1:4': 7.0,
  '1:8': 10.5,
  '1:16': 14.0,
  '1:32': 17.5,
  '1:64': 21.0
}

export const ATENUACION_DESBALANCEADO: Record<RatioDesbalanceado, { alto: number; bajo: number }> = {
  '90/10': { alto: 0.6, bajo: 10.5 },
  '85/15': { alto: 1.0, bajo: 8.5 },
  '80/20': { alto: 1.2, bajo: 7.2 },
  '70/30': { alto: 1.8, bajo: 5.5 },
  '60/40': { alto: 2.3, bajo: 4.2 },
  '50/50': { alto: 3.2, bajo: 3.2 }
}

export const ATENUACION_EMPALME_FUSION = 0.1 // dB
export const ATENUACION_CONECTOR = 0.5 // dB

// ============================================================================
// FUNCIONES DE CREACIÓN DE SPLITTER
// ============================================================================

/**
 * Crea un nuevo splitter vacío con sus puertos de salida
 */
function generateSimpleId(): string {
  return Math.random().toString(36).substring(2, 10).toUpperCase()
}

export function crearSplitter(params: {
  nodoId: string
  tipo: TipoSplitter
  ratio: RatioSplitter
  nivel: NivelSplitter
  ratioDesbalanceado?: RatioDesbalanceado
  entrada: SplitterEntrada
  bandeja?: number
  posicion?: number
}): SplitterCompleto {
  const { nodoId, tipo, ratio, nivel, ratioDesbalanceado, entrada, bandeja, posicion } = params

  // Un splitter desbalanceado (tap) siempre tiene 2 salidas físicas: cascada y local.
  // `ratio` (1:2..1:64) solo aplica a splitters balanceados.
  const numSalidas = tipo === 'desbalanceado' ? 2 : parseInt(ratio.split(':')[1])
  const atenuacionBase = tipo === 'balanceado' 
    ? ATENUACION_SPLITTER[ratio]
    : 0 // Para desbalanceados, cada puerto tiene su propia atenuación
  
  const salidas: SplitterPortSalida[] = Array.from({ length: numSalidas }, (_, i) => {
    const puerto = i + 1
    let atenuacion = atenuacionBase
    
    if (tipo === 'desbalanceado' && ratioDesbalanceado) {
      const config = ATENUACION_DESBALANCEADO[ratioDesbalanceado]
      atenuacion = puerto === 1 ? config.alto : config.bajo
    }
    
    return {
      numero: puerto,
      colorPigtail: TIA_598_COLORES[(puerto - 1) % 12],
      estado: 'libre' as const,
      atenuacion
    }
  })
  
  return {
    id: `SP-${generateSimpleId()}`,
    tipo,
    ratio,
    ratioDesbalanceado: tipo === 'desbalanceado' ? ratioDesbalanceado : undefined,
    nivel,
    nodoId,
    bandeja,
    posicion,
    entrada,
    salidas,
    atenuacionBase
  }
}

// ============================================================================
// FUNCIONES DE SANGRADO
// ============================================================================

export interface ParamsSangrado {
  nodoId: string
  tipoNodo: 'CIERRE' | 'NAP'
  
  // Fibra a sangrar
  cableOrigenId: string
  tuboOrigen: number
  hiloOrigen: number
  colorTubo: ColorBuffer
  colorHilo: ColorFibra
  
  // Configuración del splitter
  splitterConfig: {
    tipo: TipoSplitter
    ratio: RatioSplitter
    ratioDesbalanceado?: RatioDesbalanceado
    nivel: NivelSplitter
  }
  
  // Destinos de las salidas (puede estar vacío inicialmente)
  destinos?: Array<{
    puertoSplitter: number
    tipo: 'empalme_cable' | 'puerto_nap' | 'cliente_directo'
    cableId?: string
    bufferNum?: number
    fibraNum?: number
    puertoNapNum?: number
    clienteOntId?: string
    nombreCliente?: string
  }>
  
  // Ubicación física
  bandeja?: number
  posicion?: number
}

export interface ResultadoSangrado {
  splitter: SplitterCompleto
  conexionEntrada: ConexionInterna
  conexionMuerta: ConexionInterna
  conexionesSalida: ConexionInterna[]
  fibraOrigenModificada: Partial<Fibra>
  fibraMuertaModificada: Partial<Fibra>
}

/**
 * Crea un sangrado completo: corta la fibra, conecta al splitter, marca lado muerto
 */
export function crearSangrado(params: ParamsSangrado): ResultadoSangrado {
  const {
    nodoId,
    cableOrigenId,
    tuboOrigen,
    hiloOrigen,
    colorTubo,
    colorHilo,
    splitterConfig,
    destinos = [],
    bandeja,
    posicion
  } = params
  
  const ahora = new Date()
  
  // 1. Crear entrada del splitter
  const entrada: SplitterEntrada = {
    cableId: cableOrigenId,
    bufferNum: tuboOrigen,
    fibraNum: hiloOrigen,
    colorBuffer: colorTubo,
    colorFibra: colorHilo,
    estadoFibraOrigen: 'derivado'
  }
  
  // 2. Crear el splitter con puertos vacíos
  const splitter = crearSplitter({
    nodoId,
    tipo: splitterConfig.tipo,
    ratio: splitterConfig.ratio,
    nivel: splitterConfig.nivel,
    ratioDesbalanceado: splitterConfig.ratioDesbalanceado,
    entrada,
    bandeja,
    posicion
  })
  
  // 3. Agregar información de fibra muerta
  splitter.fibraMuerta = {
    cableId: cableOrigenId,
    bufferNum: tuboOrigen,
    fibraNum: hiloOrigen,
    motivo: `Sangrado para alimentar splitter ${splitter.id}`
  }
  
  // 4. Crear conexión de entrada (fibra → splitter)
  const origenConexion: OrigenConexion = {
    cableId: cableOrigenId,
    tubo: tuboOrigen,
    hilo: hiloOrigen,
    colorTubo: colorTubo,
    colorHilo: colorHilo
  }
  
  const conexionEntrada: ConexionInterna = {
    id: generarIdConexion(nodoId, origenConexion) + '-IN',
    nodoId,
    tipo: 'sangrado_splitter',
    origen: origenConexion,
    destino: {
      tipo: 'splitter_entrada',
      splitterId: splitter.id
    },
    bandeja,
    posicion,
    atenuacion: ATENUACION_EMPALME_FUSION,
    fechaCreacion: ahora
  }
  
  // 5. Crear conexión de fibra muerta (lado hacia cliente del corte)
  const conexionMuerta: ConexionInterna = {
    id: generarIdConexion(nodoId, origenConexion) + '-DEAD',
    nodoId,
    tipo: 'muerto',
    origen: origenConexion,
    destino: {
      tipo: 'muerto',
      motivo: `Lado hacia cliente - sangrado en splitter ${splitter.id}`,
      direccion: 'hacia_cliente'
    },
    bandeja,
    fechaCreacion: ahora
  }
  
  // 6. Crear conexiones de salida si hay destinos definidos
  const conexionesSalida: ConexionInterna[] = []
  
  for (const destino of destinos) {
    const puertoSalida = splitter.salidas.find(s => s.numero === destino.puertoSplitter)
    if (!puertoSalida) continue
    
    // Marcar puerto como ocupado
    puertoSalida.estado = 'ocupado'
    puertoSalida.destino = {
      tipo: destino.tipo,
      cableId: destino.cableId,
      bufferNum: destino.bufferNum,
      fibraNum: destino.fibraNum,
      puertoNapNum: destino.puertoNapNum,
      clienteOntId: destino.clienteOntId,
      nombreCliente: destino.nombreCliente
    }
    
    // Crear conexión según tipo de destino
    let destinoConexion: DestinoConexion
    
    if (destino.tipo === 'empalme_cable' && destino.cableId) {
      destinoConexion = {
        tipo: 'cable',
        cableId: destino.cableId,
        tubo: destino.bufferNum || 1,
        hilo: destino.fibraNum || 1
      }
    } else if (destino.tipo === 'puerto_nap' && destino.puertoNapNum) {
      destinoConexion = {
        tipo: 'puerto_nap',
        numeroPuerto: destino.puertoNapNum
      }
    } else {
      continue // Skip invalid destinations
    }
    
    const conexionSalida: ConexionInterna = {
      id: `${splitter.id}-OUT-${destino.puertoSplitter}`,
      nodoId,
      tipo: 'salida_splitter',
      origen: {
        cableId: splitter.id, // El "cable" es el splitter en este caso
        tubo: 0, // No aplica para splitter
        hilo: destino.puertoSplitter,
        colorTubo: 'azul', // No aplica
        colorHilo: puertoSalida.colorPigtail as ColorFibra
      },
      destino: destinoConexion,
      atenuacion: puertoSalida.atenuacion + ATENUACION_EMPALME_FUSION,
      fechaCreacion: ahora
    }
    
    conexionesSalida.push(conexionSalida)
  }
  
  // 7. Preparar modificaciones a las fibras
  const fibraOrigenModificada: Partial<Fibra> = {
    estado: 'derivado' as EstadoFibra,
    direccion: 'hacia_olt',
    splitterConectado: {
      splitterId: splitter.id,
      tipoPuerto: 'entrada'
    }
  }
  
  const fibraMuertaModificada: Partial<Fibra> = {
    estado: 'muerto' as EstadoFibra,
    direccion: 'hacia_cliente',
    notas: `Muerto por sangrado - Splitter ${splitter.id}`
  }
  
  return {
    splitter,
    conexionEntrada,
    conexionMuerta,
    conexionesSalida,
    fibraOrigenModificada,
    fibraMuertaModificada
  }
}

// ============================================================================
// FUNCIONES DE CONEXIÓN DIRECTA (PASO Y FUSIÓN)
// ============================================================================

/**
 * Crea una conexión de paso (fibra pasa sin modificación)
 */
export function crearConexionPaso(params: {
  nodoId: string
  origen: OrigenConexion
  destino: { cableId: string; tubo: number; hilo: number }
  bandeja?: number
}): ConexionInterna {
  return {
    id: generarIdConexion(params.nodoId, params.origen) + '-PASO',
    nodoId: params.nodoId,
    tipo: 'paso',
    origen: params.origen,
    destino: {
      tipo: 'cable',
      ...params.destino
    },
    bandeja: params.bandeja,
    atenuacion: 0, // Paso directo sin atenuación adicional
    fechaCreacion: new Date()
  }
}

/**
 * Crea una conexión de fusión (empalme fibra a fibra)
 */
export function crearConexionFusion(params: {
  nodoId: string
  origen: OrigenConexion
  destino: { cableId: string; tubo: number; hilo: number; colorTubo?: ColorBuffer; colorHilo?: ColorFibra }
  bandeja?: number
  tecnico?: string
}): ConexionInterna {
  return {
    id: generarIdConexion(params.nodoId, params.origen) + '-FUS',
    nodoId: params.nodoId,
    tipo: 'fusion',
    origen: params.origen,
    destino: {
      tipo: 'cable',
      ...params.destino
    },
    bandeja: params.bandeja,
    atenuacion: ATENUACION_EMPALME_FUSION,
    tecnico: params.tecnico,
    fechaCreacion: new Date()
  }
}

/**
 * Crea una conexión de reserva (fibra en bucle)
 */
export function crearConexionReserva(params: {
  nodoId: string
  origen: OrigenConexion
  longitudReserva?: number
  ubicacion?: string
  bandeja?: number
}): ConexionInterna {
  return {
    id: generarIdConexion(params.nodoId, params.origen) + '-RES',
    nodoId: params.nodoId,
    tipo: 'reserva',
    origen: params.origen,
    destino: {
      tipo: 'reserva',
      longitudReserva: params.longitudReserva,
      ubicacion: params.ubicacion
    },
    bandeja: params.bandeja,
    fechaCreacion: new Date()
  }
}

// ============================================================================
// FUNCIONES DE ASIGNACIÓN DE PUERTOS NAP
// ============================================================================

/**
 * Asigna un cliente a un puerto NAP
 */
export function asignarClienteAPuertoNAP(
  puerto: PuertoNAP,
  cliente: {
    clienteId: string
    nombreCliente: string
    ontId?: string
    ontModelo?: string
    paqueteServicio?: string
  }
): PuertoNAP {
  return {
    ...puerto,
    estado: 'ocupado',
    cliente: {
      clienteId: cliente.clienteId,
      nombreCliente: cliente.nombreCliente,
      ontId: cliente.ontId,
      ontModelo: cliente.ontModelo,
      paqueteServicio: cliente.paqueteServicio
    },
    clienteId: cliente.clienteId, // Legacy
    fechaInstalacion: new Date()
  }
}

/**
 * Libera un puerto NAP
 */
export function liberarPuertoNAP(puerto: PuertoNAP): PuertoNAP {
  return {
    ...puerto,
    estado: 'libre',
    cliente: undefined,
    clienteId: null,
    fechaInstalacion: undefined
  }
}

/**
 * Conecta un puerto NAP a una salida de splitter
 */
export function conectarPuertoAASplitter(
  puerto: PuertoNAP,
  splitterId: string,
  puertoSplitter: number,
  colorPigtail: string,
  atenuacion: number
): PuertoNAP {
  return {
    ...puerto,
    conexionSplitter: {
      splitterId,
      puertoSplitter,
      colorPigtail,
      atenuacion
    }
  }
}

// ============================================================================
// VALIDACIÓN DE CONEXIONES
// ============================================================================

/**
 * Valida la integridad de todas las conexiones en un nodo
 */
export function validarConexionesNodo(
  conexiones: ConexionInterna[],
  splitters: SplitterCompleto[],
  puertos?: PuertoNAP[]
): ValidacionConexiones {
  const errores: ErrorConexion[] = []
  const advertencias: AdvertenciaConexion[] = []
  
  // 1. Verificar que cada splitter tenga entrada definida
  for (const splitter of splitters) {
    if (!splitter.entrada || !splitter.entrada.cableId) {
      errores.push({
        tipo: 'splitter_sin_entrada',
        mensaje: `Splitter ${splitter.id} no tiene fibra de entrada definida`,
        elementoId: splitter.id
      })
    }
  }
  
  // 2. Verificar que no haya fibras huérfanas (sin destino)
  const fibrasConDestino = new Set<string>()
  for (const conexion of conexiones) {
    const fibraId = `${conexion.origen.cableId}-${conexion.origen.tubo}-${conexion.origen.hilo}`
    fibrasConDestino.add(fibraId)
  }
  
  // 3. Verificar puertos NAP duplicados
  if (puertos) {
    const puertosOcupados = new Map<number, string>()
    for (const puerto of puertos) {
      if (puerto.estado === 'ocupado' && puerto.cliente) {
        if (puertosOcupados.has(puerto.puerto)) {
          errores.push({
            tipo: 'puerto_duplicado',
            mensaje: `Puerto ${puerto.puerto} tiene múltiples clientes asignados`,
            elementoId: `puerto-${puerto.puerto}`
          })
        }
        puertosOcupados.set(puerto.puerto, puerto.cliente.clienteId)
      }
    }
    
    // Advertir sobre puertos sin conexión de splitter
    for (const puerto of puertos) {
      if (puerto.estado === 'ocupado' && !puerto.conexionSplitter) {
        advertencias.push({
          tipo: 'fibra_sin_destino',
          mensaje: `Puerto ${puerto.puerto} tiene cliente pero no conexión de splitter`,
          elementoId: `puerto-${puerto.puerto}`
        })
      }
    }
  }
  
  // 4. Verificar atenuación total excesiva
  for (const splitter of splitters) {
    for (const salida of splitter.salidas) {
      if (salida.atenuacion > 20) {
        advertencias.push({
          tipo: 'atenuacion_alta',
          mensaje: `Splitter ${splitter.id} puerto ${salida.numero} tiene atenuación muy alta: ${salida.atenuacion} dB`,
          elementoId: `${splitter.id}-${salida.numero}`
        })
      }
    }
  }
  
  // 5. Contar fibras en reserva excesiva
  const fibrasReserva = conexiones.filter(c => c.tipo === 'reserva')
  if (fibrasReserva.length > 12) {
    advertencias.push({
      tipo: 'reserva_excesiva',
      mensaje: `Hay ${fibrasReserva.length} fibras en reserva, considere utilizarlas`
    })
  }
  
  return {
    valido: errores.length === 0,
    errores,
    advertencias
  }
}

// ============================================================================
// ESTADÍSTICAS DE NODO
// ============================================================================

export interface EstadisticasConexiones {
  totalFibrasEntrantes: number
  fibrasEnPaso: number
  fibrasFusionadas: number
  fibrasDerivadas: number
  fibrasMuertas: number
  fibrasReserva: number
  puertosNAPOcupados: number
  puertosNAPLibres: number
  splittersInstalados: number
  atenuacionPromedio: number
}

/**
 * Calcula estadísticas de conexiones de un nodo
 */
export function calcularEstadisticasConexiones(
  conexiones: ConexionInterna[],
  splitters: SplitterCompleto[],
  puertos: PuertoNAP[]
): EstadisticasConexiones {
  const stats: EstadisticasConexiones = {
    totalFibrasEntrantes: 0,
    fibrasEnPaso: 0,
    fibrasFusionadas: 0,
    fibrasDerivadas: 0,
    fibrasMuertas: 0,
    fibrasReserva: 0,
    puertosNAPOcupados: 0,
    puertosNAPLibres: 0,
    splittersInstalados: splitters.length,
    atenuacionPromedio: 0
  }
  
  // Contar por tipo de conexión
  for (const conexion of conexiones) {
    stats.totalFibrasEntrantes++
    
    switch (conexion.tipo) {
      case 'paso':
        stats.fibrasEnPaso++
        break
      case 'fusion':
        stats.fibrasFusionadas++
        break
      case 'sangrado_splitter':
        stats.fibrasDerivadas++
        break
      case 'muerto':
        stats.fibrasMuertas++
        break
      case 'reserva':
        stats.fibrasReserva++
        break
    }
  }
  
  // Contar puertos
  for (const puerto of puertos) {
    if (puerto.estado === 'ocupado') {
      stats.puertosNAPOcupados++
    } else if (puerto.estado === 'libre') {
      stats.puertosNAPLibres++
    }
  }
  
  // Calcular atenuación promedio de splitters
  if (splitters.length > 0) {
    const totalAtenuacion = splitters.reduce((sum, sp) => sum + sp.atenuacionBase, 0)
    stats.atenuacionPromedio = totalAtenuacion / splitters.length
  }
  
  return stats
}

// ============================================================================
// UTILIDADES DE COLOR
// ============================================================================

/**
 * Convierte número de posición a color TIA-598
 */
export function posicionAColor(posicion: number): ColorFibra {
  return TIA_598_COLORES[(posicion - 1) % 12]
}

/**
 * Convierte color a número de posición TIA-598
 */
export function colorAPosicion(color: ColorFibra): number {
  const index = TIA_598_COLORES.indexOf(color)
  return index >= 0 ? index + 1 : 1
}

/**
 * Genera array de colores para N salidas de splitter
 */
export function generarColoresSalidas(numSalidas: number): ColorFibra[] {
  return Array.from({ length: numSalidas }, (_, i) => TIA_598_COLORES[i % 12])
}

// ============================================================================
// FUNCIONES DE ACTUALIZACIÓN DE ESTADO DE FIBRAS
// ============================================================================

/**
 * Estructura de fibra dentro de un cable
 */
export interface FibraEnCable {
  numero: number
  color: string
  fibraId: string
  /** Estado real, por extremo del cable. Ver EstadoExtremoFibra. */
  extremos?: {
    origen: EstadoExtremoFibra
    destino: EstadoExtremoFibra
  }
  // Deprecados: agregado de los dos extremos, mantenido por compatibilidad
  // con formularios que aún no leen `extremos` (ver comentario en types/fibers.ts).
  estado: EstadoFibra
  conectadaA: string | null
  splitterConectado?: {
    splitterId: string
    tipoPuerto: 'entrada' | 'salida'
    numeroPuerto?: number
  } | null
}

export interface BufferEnCable {
  numero: number
  color: string
  fibras: FibraEnCable[]
}

export interface CableConEstructura {
  id: string
  tipo: string
  numeroFibras: number
  tieneBuffers: boolean
  buffers?: BufferEnCable[] | null
  fibras?: FibraEnCable[] | null
  // Necesarios para saber qué extremo (origen/destino) toca un nodo dado.
  origen?: unknown
  origenId?: string
  destino?: unknown
  destinoId?: string
}

// ============================================================================
// ESTADO DE FIBRA POR EXTREMO
// ============================================================================

const EXTREMO_LIBRE: EstadoExtremoFibra = { estado: 'libre', conectadoA: null, splitterConectado: null }

function extremoLibre(): EstadoExtremoFibra {
  return { ...EXTREMO_LIBRE }
}

/**
 * El campo `estado`/`conectadaA` a nivel de fibra es un valor DERIVADO de sus
 * dos extremos, mantenido solo para no romper los formularios que todavía no
 * migraron a leer `extremos` directamente. Regla: si algún extremo no está
 * libre, ese es el que se agrega (el origen gana en caso de empate).
 */
function agregarExtremos(fibra: FibraEnCable): Pick<FibraEnCable, 'estado' | 'conectadaA' | 'splitterConectado'> {
  const { origen, destino } = fibra.extremos || { origen: extremoLibre(), destino: extremoLibre() }
  const ganador = origen.estado !== 'libre' ? origen : destino.estado !== 'libre' ? destino : origen
  return {
    estado: ganador.estado,
    conectadaA: ganador.conectadoA,
    splitterConectado: ganador.splitterConectado ?? null,
  }
}

/**
 * Ubica una fibra dentro de un cable (por buffer+número o número suelto) y le
 * aplica `actualizar` solo al extremo indicado, recalculando el agregado
 * deprecado. No muta el cable de entrada -- devuelve una copia.
 */
export function mutarExtremoFibra(
  cable: CableConEstructura,
  extremo: 'origen' | 'destino',
  bufferNum: number | null,
  fibraNum: number,
  actualizar: Partial<EstadoExtremoFibra>
): CableConEstructura {
  const aplicar = (fibra: FibraEnCable): FibraEnCable => {
    if (fibra.numero !== fibraNum) return fibra
    const extremos = fibra.extremos || { origen: extremoLibre(), destino: extremoLibre() }
    const nuevosExtremos = {
      ...extremos,
      [extremo]: { ...extremos[extremo], ...actualizar },
    }
    const conFibra: FibraEnCable = { ...fibra, extremos: nuevosExtremos }
    return { ...conFibra, ...agregarExtremos(conFibra) }
  }

  const cableActualizado = { ...cable }
  if (cable.tieneBuffers && cable.buffers) {
    cableActualizado.buffers = cable.buffers.map((buffer) =>
      buffer.numero === bufferNum ? { ...buffer, fibras: buffer.fibras.map(aplicar) } : buffer
    )
  } else if (cable.fibras) {
    cableActualizado.fibras = cable.fibras.map(aplicar)
  }
  return cableActualizado
}

/**
 * Fusiona dos fibras (posiblemente del mismo cable) en el extremo correcto de
 * cada una. A diferencia de `fusionarFibras` (que pisa el estado global de la
 * fibra entera), esto respeta que el otro extremo de cada cable puede seguir
 * usándose para otra cosa.
 */
export function fusionarFibrasPorExtremo(params: {
  cableOrigen: CableConEstructura
  extremoOrigen: 'origen' | 'destino'
  bufferOrigenNum: number | null
  fibraOrigenNum: number
  fibraIdOrigen: string
  cableDestino: CableConEstructura
  extremoDestino: 'origen' | 'destino'
  bufferDestinoNum: number | null
  fibraDestinoNum: number
  fibraIdDestino: string
}): { cableOrigen: CableConEstructura; cableDestino: CableConEstructura } {
  const {
    cableOrigen, extremoOrigen, bufferOrigenNum, fibraOrigenNum, fibraIdOrigen,
    cableDestino, extremoDestino, bufferDestinoNum, fibraDestinoNum, fibraIdDestino,
  } = params

  if (cableOrigen.id === cableDestino.id) {
    // Mismo cable: encadenar las dos mutaciones sobre la misma copia para no
    // perder una al aplicar la otra.
    const paso1 = mutarExtremoFibra(cableOrigen, extremoOrigen, bufferOrigenNum, fibraOrigenNum, {
      estado: 'fusionado',
      conectadoA: fibraIdDestino,
    })
    const combinado = mutarExtremoFibra(paso1, extremoDestino, bufferDestinoNum, fibraDestinoNum, {
      estado: 'fusionado',
      conectadoA: fibraIdOrigen,
    })
    return { cableOrigen: combinado, cableDestino: combinado }
  }

  return {
    cableOrigen: mutarExtremoFibra(cableOrigen, extremoOrigen, bufferOrigenNum, fibraOrigenNum, {
      estado: 'fusionado',
      conectadoA: fibraIdDestino,
    }),
    cableDestino: mutarExtremoFibra(cableDestino, extremoDestino, bufferDestinoNum, fibraDestinoNum, {
      estado: 'fusionado',
      conectadoA: fibraIdOrigen,
    }),
  }
}

/** Libera un extremo específico de una fibra (lo devuelve a 'libre'). */
export function liberarFibraPorExtremo(
  cable: CableConEstructura,
  extremo: 'origen' | 'destino',
  bufferNum: number | null,
  fibraNum: number
): CableConEstructura {
  return mutarExtremoFibra(cable, extremo, bufferNum, fibraNum, extremoLibre())
}

export { cableEndpointAtNode }

/**
 * Marca una fibra específica como derivada (conectada a entrada de splitter)
 */
export function derivarFibraASplitter(
  cable: CableConEstructura,
  bufferNum: number | null,
  fibraNum: number,
  splitterId: string
): CableConEstructura {
  const cableActualizado = { ...cable }

  if (cable.tieneBuffers && cable.buffers) {
    cableActualizado.buffers = cable.buffers.map(buffer => {
      if (buffer.numero === bufferNum) {
        return {
          ...buffer,
          fibras: buffer.fibras.map(fibra => {
            if (fibra.numero === fibraNum) {
              return {
                ...fibra,
                estado: 'derivado' as EstadoFibra,
                splitterConectado: {
                  splitterId,
                  tipoPuerto: 'entrada' as const
                }
              }
            }
            return fibra
          })
        }
      }
      return buffer
    })
  } else if (cable.fibras) {
    cableActualizado.fibras = cable.fibras.map(fibra => {
      if (fibra.numero === fibraNum) {
        return {
          ...fibra,
          estado: 'derivado' as EstadoFibra,
          splitterConectado: {
            splitterId,
            tipoPuerto: 'entrada' as const
          }
        }
      }
      return fibra
    })
  }

  return cableActualizado
}

/**
 * Conecta una salida de splitter a una fibra de otro cable
 */
export function conectarSalidaSplitterAFibra(
  cable: CableConEstructura,
  bufferNum: number | null,
  fibraNum: number,
  splitterId: string,
  puertoSalida: number
): CableConEstructura {
  const cableActualizado = { ...cable }

  const actualizarFibra = (fibra: FibraEnCable): FibraEnCable => {
    if (fibra.numero === fibraNum) {
      return {
        ...fibra,
        estado: 'fusionado' as EstadoFibra,
        splitterConectado: {
          splitterId,
          tipoPuerto: 'salida' as const,
          numeroPuerto: puertoSalida
        }
      }
    }
    return fibra
  }

  if (cable.tieneBuffers && cable.buffers) {
    cableActualizado.buffers = cable.buffers.map(buffer => {
      if (buffer.numero === bufferNum) {
        return {
          ...buffer,
          fibras: buffer.fibras.map(actualizarFibra)
        }
      }
      return buffer
    })
  } else if (cable.fibras) {
    cableActualizado.fibras = cable.fibras.map(actualizarFibra)
  }

  return cableActualizado
}

/**
 * Marca fibras como "en paso" (pasan por el nodo sin ser tocadas)
 */
export function marcarFibrasEnPaso(
  cable: CableConEstructura,
  bufferNum: number | null,
  fibrasNums: number[]
): CableConEstructura {
  const cableActualizado = { ...cable }

  const actualizarFibra = (fibra: FibraEnCable): FibraEnCable => {
    if (fibrasNums.includes(fibra.numero) && fibra.estado === 'libre') {
      return {
        ...fibra,
        estado: 'en_paso' as EstadoFibra
      }
    }
    return fibra
  }

  if (cable.tieneBuffers && cable.buffers) {
    cableActualizado.buffers = cable.buffers.map(buffer => {
      if (bufferNum === null || buffer.numero === bufferNum) {
        return {
          ...buffer,
          fibras: buffer.fibras.map(actualizarFibra)
        }
      }
      return buffer
    })
  } else if (cable.fibras) {
    cableActualizado.fibras = cable.fibras.map(actualizarFibra)
  }

  return cableActualizado
}

/**
 * Marca fibras como reserva (para uso futuro)
 */
export function marcarFibrasReserva(
  cable: CableConEstructura,
  bufferNum: number | null,
  fibrasNums: number[]
): CableConEstructura {
  const cableActualizado = { ...cable }

  const actualizarFibra = (fibra: FibraEnCable): FibraEnCable => {
    if (fibrasNums.includes(fibra.numero) && fibra.estado === 'libre') {
      return {
        ...fibra,
        estado: 'reserva' as EstadoFibra
      }
    }
    return fibra
  }

  if (cable.tieneBuffers && cable.buffers) {
    cableActualizado.buffers = cable.buffers.map(buffer => {
      if (bufferNum === null || buffer.numero === bufferNum) {
        return {
          ...buffer,
          fibras: buffer.fibras.map(actualizarFibra)
        }
      }
      return buffer
    })
  } else if (cable.fibras) {
    cableActualizado.fibras = cable.fibras.map(actualizarFibra)
  }

  return cableActualizado
}

/**
 * Libera una fibra (vuelve a estado libre)
 */
export function liberarFibra(
  cable: CableConEstructura,
  bufferNum: number | null,
  fibraNum: number
): CableConEstructura {
  const cableActualizado = { ...cable }

  const actualizarFibra = (fibra: FibraEnCable): FibraEnCable => {
    if (fibra.numero === fibraNum) {
      return {
        ...fibra,
        estado: 'libre' as EstadoFibra,
        conectadaA: null,
        splitterConectado: null
      }
    }
    return fibra
  }

  if (cable.tieneBuffers && cable.buffers) {
    cableActualizado.buffers = cable.buffers.map(buffer => {
      if (buffer.numero === bufferNum) {
        return {
          ...buffer,
          fibras: buffer.fibras.map(actualizarFibra)
        }
      }
      return buffer
    })
  } else if (cable.fibras) {
    cableActualizado.fibras = cable.fibras.map(actualizarFibra)
  }

  return cableActualizado
}

/**
 * Conecta una fibra de un cable directamente a otra fibra de otro cable (empalme/fusión)
 */
export function fusionarFibras(
  cableOrigen: CableConEstructura,
  bufferOrigenNum: number | null,
  fibraOrigenNum: number,
  cableDestino: CableConEstructura,
  bufferDestinoNum: number | null,
  fibraDestinoNum: number
): { cableOrigen: CableConEstructura; cableDestino: CableConEstructura } {
  // Encontrar fibraId del destino
  let fibraIdDestino: string | null = null
  
  if (cableDestino.tieneBuffers && cableDestino.buffers) {
    const buffer = cableDestino.buffers.find(b => b.numero === bufferDestinoNum)
    const fibra = buffer?.fibras.find(f => f.numero === fibraDestinoNum)
    fibraIdDestino = fibra?.fibraId || null
  } else if (cableDestino.fibras) {
    const fibra = cableDestino.fibras.find(f => f.numero === fibraDestinoNum)
    fibraIdDestino = fibra?.fibraId || null
  }

  // Encontrar fibraId del origen
  let fibraIdOrigen: string | null = null
  
  if (cableOrigen.tieneBuffers && cableOrigen.buffers) {
    const buffer = cableOrigen.buffers.find(b => b.numero === bufferOrigenNum)
    const fibra = buffer?.fibras.find(f => f.numero === fibraOrigenNum)
    fibraIdOrigen = fibra?.fibraId || null
  } else if (cableOrigen.fibras) {
    const fibra = cableOrigen.fibras.find(f => f.numero === fibraOrigenNum)
    fibraIdOrigen = fibra?.fibraId || null
  }

  // Actualizar cable origen
  const actualizarFibraOrigen = (fibra: FibraEnCable): FibraEnCable => {
    if (fibra.numero === fibraOrigenNum) {
      return {
        ...fibra,
        estado: 'fusionado' as EstadoFibra,
        conectadaA: fibraIdDestino
      }
    }
    return fibra
  }

  // Actualizar cable destino
  const actualizarFibraDestino = (fibra: FibraEnCable): FibraEnCable => {
    if (fibra.numero === fibraDestinoNum) {
      return {
        ...fibra,
        estado: 'fusionado' as EstadoFibra,
        conectadaA: fibraIdOrigen
      }
    }
    return fibra
  }

  const cableOrigenActualizado = { ...cableOrigen }
  const cableDestinoActualizado = { ...cableDestino }

  if (cableOrigen.tieneBuffers && cableOrigen.buffers) {
    cableOrigenActualizado.buffers = cableOrigen.buffers.map(buffer => {
      if (buffer.numero === bufferOrigenNum) {
        return { ...buffer, fibras: buffer.fibras.map(actualizarFibraOrigen) }
      }
      return buffer
    })
  } else if (cableOrigen.fibras) {
    cableOrigenActualizado.fibras = cableOrigen.fibras.map(actualizarFibraOrigen)
  }

  if (cableDestino.tieneBuffers && cableDestino.buffers) {
    cableDestinoActualizado.buffers = cableDestino.buffers.map(buffer => {
      if (buffer.numero === bufferDestinoNum) {
        return { ...buffer, fibras: buffer.fibras.map(actualizarFibraDestino) }
      }
      return buffer
    })
  } else if (cableDestino.fibras) {
    cableDestinoActualizado.fibras = cableDestino.fibras.map(actualizarFibraDestino)
  }

  return {
    cableOrigen: cableOrigenActualizado,
    cableDestino: cableDestinoActualizado
  }
}

/**
 * Obtiene el resumen de estado de fibras de un cable
 */
export function obtenerResumenFibrasCable(cable: CableConEstructura): {
  total: number
  libres: number
  enPaso: number
  fusionadas: number
  derivadas: number
  muertas: number
  reserva: number
  cliente: number
} {
  const resumen = {
    total: 0,
    libres: 0,
    enPaso: 0,
    fusionadas: 0,
    derivadas: 0,
    muertas: 0,
    reserva: 0,
    cliente: 0
  }

  const contarFibra = (fibra: FibraEnCable) => {
    resumen.total++
    switch (fibra.estado) {
      case 'libre': resumen.libres++; break
      case 'en_paso': resumen.enPaso++; break
      case 'fusionado': resumen.fusionadas++; break
      case 'derivado': resumen.derivadas++; break
      case 'muerto': resumen.muertas++; break
      case 'reserva': resumen.reserva++; break
      case 'cliente': resumen.cliente++; break
    }
  }

  if (cable.tieneBuffers && cable.buffers) {
    cable.buffers.forEach(buffer => {
      buffer.fibras.forEach(contarFibra)
    })
  } else if (cable.fibras) {
    cable.fibras.forEach(contarFibra)
  }

  return resumen
}

// ============================================================================
// FUNCIONES DE INTEGRACIÓN CON API
// ============================================================================

/**
 * Crea una conexión de fusión y la sincroniza con el backend
 */
export async function crearFusionConPersistencia(params: {
  nodeId: string
  cableOrigenId: string
  bufferOrigen: number | null
  fibraOrigen: number
  cableDestinoId: string
  bufferDestino: number | null
  fibraDestino: number
  atenuacion?: number
  observaciones?: string
}): Promise<{ 
  connection: any 
  cableOrigenActualizado: CableConEstructura 
  cableDestinoActualizado: CableConEstructura 
}> {
  const { 
    nodeId, 
    cableOrigenId, 
    bufferOrigen, 
    fibraOrigen,
    cableDestinoId,
    bufferDestino,
    fibraDestino,
    atenuacion = ATENUACION_EMPALME_FUSION,
    observaciones
  } = params
  
  // 1. Crear conexión en el backend
  const connectionData: CreateConnectionData = {
    tipo: 'FUSION',
    cableEntradaId: cableOrigenId,
    fibraEntrada: fibraOrigen,
    bufferEntrada: bufferOrigen || undefined,
    cableSalidaId: cableDestinoId,
    fibraSalida: fibraDestino,
    bufferSalida: bufferDestino || undefined,
    nodeId,
    atenuacion,
    observaciones
  }
  
  const connection = await connectionServiceApi.create(connectionData)
  
  // 2. Obtener cables actuales
  const [cableOrigen, cableDestino] = await Promise.all([
    cableServiceApi.getById(cableOrigenId),
    cableServiceApi.getById(cableDestinoId)
  ])
  
  // 3. Actualizar estado de fibras localmente
  const { cableOrigen: origenActualizado, cableDestino: destinoActualizado } = fusionarFibras(
    cableOrigen as CableConEstructura,
    bufferOrigen,
    fibraOrigen,
    cableDestino as CableConEstructura,
    bufferDestino,
    fibraDestino
  )
  
  // 4. Persistir cambios en los cables
  await Promise.all([
    cableServiceApi.update(cableOrigenId, {
      buffers: origenActualizado.buffers as any,
      fibras: origenActualizado.fibras as any
    }),
    cableServiceApi.update(cableDestinoId, {
      buffers: destinoActualizado.buffers as any,
      fibras: destinoActualizado.fibras as any
    })
  ])
  
  return {
    connection,
    cableOrigenActualizado: origenActualizado,
    cableDestinoActualizado: destinoActualizado
  }
}

/**
 * Elimina una conexión de fusión y actualiza los cables
 */
export async function eliminarFusionConPersistencia(params: {
  connectionId: string
  cableOrigenId: string
  bufferOrigen: number | null
  fibraOrigen: number
  cableDestinoId: string
  bufferDestino: number | null
  fibraDestino: number
}): Promise<void> {
  const {
    connectionId,
    cableOrigenId,
    bufferOrigen,
    fibraOrigen,
    cableDestinoId,
    bufferDestino,
    fibraDestino
  } = params
  
  // 1. Eliminar conexión del backend
  await connectionServiceApi.delete(connectionId)
  
  // 2. Obtener cables actuales
  const [cableOrigen, cableDestino] = await Promise.all([
    cableServiceApi.getById(cableOrigenId),
    cableServiceApi.getById(cableDestinoId)
  ])
  
  // 3. Liberar fibras
  const origenLiberado = liberarFibra(
    cableOrigen as CableConEstructura,
    bufferOrigen,
    fibraOrigen
  )
  
  const destinoLiberado = liberarFibra(
    cableDestino as CableConEstructura,
    bufferDestino,
    fibraDestino
  )
  
  // 4. Persistir cambios
  await Promise.all([
    cableServiceApi.update(cableOrigenId, {
      buffers: origenLiberado.buffers as any,
      fibras: origenLiberado.fibras as any
    }),
    cableServiceApi.update(cableDestinoId, {
      buffers: destinoLiberado.buffers as any,
      fibras: destinoLiberado.fibras as any
    })
  ])
}

/**
 * Obtiene todas las conexiones de un nodo con información enriquecida
 */
export async function obtenerConexionesNodoEnriquecidas(nodeId: string): Promise<{
  conexiones: Array<{
    id: string
    tipo: string
    cableEntrada: { id: string; nombre: string; tipo: string }
    fibraEntrada: { numero: number; buffer?: number; color: string }
    cableSalida: { id: string; nombre: string; tipo: string }
    fibraSalida: { numero: number; buffer?: number; color: string }
    atenuacion?: number
  }>
  resumen: {
    totalFusiones: number
    totalDerivaciones: number
    atenuacionTotal: number
  }
}> {
  try {
    const result = await connectionServiceApi.getByNode(nodeId)
    
    const conexiones = result.connections.map(conn => ({
      id: conn.id,
      tipo: conn.tipo,
      cableEntrada: {
        id: conn.cableEntradaId,
        nombre: conn.cableEntradaId.slice(-8),
        tipo: 'CABLE'
      },
      fibraEntrada: {
        numero: conn.fibraEntrada,
        buffer: conn.bufferEntrada,
        color: posicionAColor(conn.fibraEntrada)
      },
      cableSalida: {
        id: conn.cableSalidaId,
        nombre: conn.cableSalidaId.slice(-8),
        tipo: 'CABLE'
      },
      fibraSalida: {
        numero: conn.fibraSalida,
        buffer: conn.bufferSalida,
        color: posicionAColor(conn.fibraSalida)
      },
      atenuacion: conn.atenuacion
    }))
    
    const resumen = {
      totalFusiones: result.connections.filter(c => c.tipo === 'FUSION').length,
      totalDerivaciones: result.connections.filter(c => c.tipo === 'SPLITTER').length,
      atenuacionTotal: result.connections.reduce((sum, c) => sum + (c.atenuacion || 0), 0)
    }
    
    return { conexiones, resumen }
  } catch (error) {
    console.warn('Error obteniendo conexiones del nodo:', error)
    return { conexiones: [], resumen: { totalFusiones: 0, totalDerivaciones: 0, atenuacionTotal: 0 } }
  }
}

/**
 * Sincroniza las conexiones locales (JSON en cable) con la tabla FiberConnection
 */
export async function sincronizarConexionesConBackend(
  cables: CableConEstructura[],
  nodeId: string
): Promise<{ created: number; errors: string[] }> {
  const conexionesACrear: CreateConnectionData[] = []
  const errors: string[] = []
  
  for (const cable of cables) {
    const procesarFibra = (fibra: FibraEnCable, bufferNum?: number) => {
      if (!fibra.conectadaA) return
      
      const parts = fibra.conectadaA.split('|')
      if (parts.length < 3) return
      
      const [cableDestinoId, bufferDestPart, colorDestPart] = parts
      
      // Buscar fibra destino por color
      const fibraDestinoNum = TIA_598_COLORES.indexOf(colorDestPart as ColorFibra) + 1
      const bufferDestinoNum = bufferDestPart === 'SB' ? undefined : TIA_598_COLORES.indexOf(bufferDestPart as ColorFibra) + 1
      
      if (fibraDestinoNum > 0) {
        conexionesACrear.push({
          tipo: fibra.estado === 'derivado' ? 'SPLITTER' : 'FUSION',
          cableEntradaId: cable.id,
          fibraEntrada: fibra.numero,
          bufferEntrada: bufferNum,
          cableSalidaId: cableDestinoId,
          fibraSalida: fibraDestinoNum,
          bufferSalida: bufferDestinoNum,
          nodeId,
          atenuacion: ATENUACION_EMPALME_FUSION
        })
      }
    }
    
    if (cable.tieneBuffers && cable.buffers) {
      cable.buffers.forEach(buffer => {
        buffer.fibras.forEach(fibra => procesarFibra(fibra, buffer.numero))
      })
    } else if (cable.fibras) {
      cable.fibras.forEach(fibra => procesarFibra(fibra))
    }
  }
  
  if (conexionesACrear.length === 0) {
    return { created: 0, errors: [] }
  }
  
  try {
    const result = await connectionServiceApi.createBulk(conexionesACrear)
    return { created: result.count, errors: [] }
  } catch (error) {
    errors.push(error instanceof Error ? error.message : 'Error desconocido')
    return { created: 0, errors }
  }
}
